import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiLightbulb, FiArrowUp, FiArrowDown, FiMinus } = FiIcons;

function HintModal({ isOpen, onClose, targetColor, userColor }) {
  if (!isOpen) return null;

  const getColorHints = () => {
    const hints = [];
    const threshold = 20;

    // Red hints
    if (Math.abs(targetColor.r - userColor.r) > threshold) {
      if (targetColor.r > userColor.r) {
        hints.push({
          color: 'Red',
          direction: 'increase',
          icon: FiArrowUp,
          bgColor: 'bg-red-500/20',
          textColor: 'text-red-400'
        });
      } else {
        hints.push({
          color: 'Red',
          direction: 'decrease',
          icon: FiArrowDown,
          bgColor: 'bg-red-500/20',
          textColor: 'text-red-400'
        });
      }
    }

    // Green hints
    if (Math.abs(targetColor.g - userColor.g) > threshold) {
      if (targetColor.g > userColor.g) {
        hints.push({
          color: 'Green',
          direction: 'increase',
          icon: FiArrowUp,
          bgColor: 'bg-green-500/20',
          textColor: 'text-green-400'
        });
      } else {
        hints.push({
          color: 'Green',
          direction: 'decrease',
          icon: FiArrowDown,
          bgColor: 'bg-green-500/20',
          textColor: 'text-green-400'
        });
      }
    }

    // Blue hints
    if (Math.abs(targetColor.b - userColor.b) > threshold) {
      if (targetColor.b > userColor.b) {
        hints.push({
          color: 'Blue',
          direction: 'increase',
          icon: FiArrowUp,
          bgColor: 'bg-blue-500/20',
          textColor: 'text-blue-400'
        });
      } else {
        hints.push({
          color: 'Blue',
          direction: 'decrease',
          icon: FiArrowDown,
          bgColor: 'bg-blue-500/20',
          textColor: 'text-blue-400'
        });
      }
    }

    return hints;
  };

  const getColorTheoryTip = () => {
    const tips = [
      "🎨 Warm colors (reds, oranges) often need more red and less blue",
      "❄️ Cool colors (blues, greens) typically have lower red values",
      "🌟 Bright colors usually have high values in multiple channels",
      "🌙 Dark colors have low values across all RGB channels",
      "💜 Purple colors mix red and blue with minimal green",
      "🧡 Orange colors are high in red, medium in green, low in blue",
      "💛 Yellow colors are high in both red and green, low in blue",
      "🖤 Gray colors have similar values across all three channels"
    ];
    
    return tips[Math.floor(Math.random() * tips.length)];
  };

  const hints = getColorHints();
  const colorTip = getColorTheoryTip();

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 50 }}
          className="bg-gradient-to-br from-yellow-900/90 to-orange-900/90 rounded-3xl p-6 w-full max-w-md border border-yellow-500/30"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiLightbulb} className="text-yellow-400 text-2xl" />
              <h2 className="text-2xl font-bold text-white">Color Hint</h2>
            </div>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="bg-white/20 p-2 rounded-xl"
            >
              <SafeIcon icon={FiX} className="text-white" />
            </motion.button>
          </div>

          {hints.length > 0 ? (
            <div className="space-y-3 mb-6">
              <p className="text-yellow-200 text-center mb-4">
                Try adjusting these colors:
              </p>
              {hints.map((hint, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`${hint.bgColor} rounded-xl p-4 flex items-center gap-3`}
                >
                  <SafeIcon icon={hint.icon} className={`${hint.textColor} text-xl`} />
                  <span className="text-white font-medium">
                    {hint.direction === 'increase' ? 'Increase' : 'Decrease'} {hint.color}
                  </span>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center mb-6">
              <div className="bg-green-500/20 rounded-xl p-4 mb-4">
                <p className="text-green-400 font-medium">
                  🎯 You're very close! Fine-tune your adjustments.
                </p>
              </div>
            </div>
          )}

          <div className="bg-white/10 rounded-xl p-4 mb-6">
            <p className="text-white text-sm">
              <span className="font-medium">💡 Color Theory Tip:</span>
              <br />
              {colorTip}
            </p>
          </div>

          <div className="bg-orange-500/20 border border-orange-500/30 rounded-xl p-3 text-center">
            <p className="text-orange-300 text-sm">
              ⚠️ Using hints reduces your score by 10 points
            </p>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

export default HintModal;